// Client-side board-grade credit-file PDF generator.
// Renders the fully-loaded loan-file object (same shape as /approved/{id})
// into a branded, paginated HTML document and triggers the browser's
// native print-to-PDF. Crisp vector text, no server load on read-only Atlas.

import { formatINRFull } from "@/lib/format";

const esc = (v) =>
  v === "" || v == null
    ? "—"
    : String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function kvTable(rows) {
  if (!rows || !rows.length) return `<p class="empty">Not available</p>`;
  return `<table class="kv">${rows
    .map((r) => `<tr><th>${esc(r.label)}</th><td>${esc(r.value)}</td></tr>`)
    .join("")}</table>`;
}

function docList(docs) {
  if (!docs || !docs.length) return "";
  return `<ul class="docs">${docs
    .map((dd) => {
      const name = dd.name || dd.label || "Document";
      const tag =
        dd.signed === undefined
          ? ""
          : dd.signed
          ? ` <span class="tag signed">SIGNED</span>`
          : ` <span class="tag unsigned">UNSIGNED</span>`;
      return `<li><a href="${esc(dd.url)}">${esc(name)}</a>${tag}</li>`;
    })
    .join("")}</ul>`;
}

// Structured summary blocks mirroring the on-screen drawer, built from the
// normalized top-level fields. Rows with no value render as "—".
function summarySections(d) {
  const inr = (v) => (v == null || v === "" ? "" : formatINRFull(v));
  const yn = (v) => (v ? "Yes" : "No");
  const has = (rows) => rows.some((r) => r.value != null && r.value !== "" && r.value !== "—");
  const blocks = [
    {
      title: "Loan Summary",
      rows: [
        { label: "Applied Loan Amount", value: inr(d.loan_amount) },
        { label: "Applied Tenor (Months)", value: d.tenor },
        { label: "Loan Type", value: d.loan_type },
        { label: "Application Type", value: d.app_type || d.status },
        { label: "MCC Sanctioned Amount", value: inr(d.mcc_loan_amt) },
        { label: "MCC Sanctioned Tenor", value: d.mcc_tenor },
        { label: "DD Sanctioned Amount", value: inr(d.dd_loan_amt) },
        { label: "DD Sanctioned Tenor", value: d.dd_tenor },
      ],
    },
    {
      title: "Company & KYC",
      rows: [
        { label: "Company Name", value: d.company },
        { label: "Company Type", value: d.company_type },
        { label: "PAN", value: d.pan },
        { label: "GST No", value: d.gst },
        { label: "CIN", value: d.cin },
        { label: "Business Nature", value: d.business_nature },
        { label: "Years in Business", value: d.years_in_business },
        { label: "PAN Name Match", value: d.pan_name_match ? "Verified" : "" },
      ],
    },
    {
      title: "Contact & Address",
      rows: [
        { label: "Primary Email", value: d.email },
        { label: "Secondary Email", value: d.secondary_email },
        { label: "Phone", value: `${d.country_code || ""} ${d.phone || ""}`.trim() },
        { label: "Alternate Phone", value: d.alt_phone },
        { label: "State", value: d.state },
        { label: "Landmark", value: d.landmark },
        { label: "Address", value: d.address },
      ],
    },
    {
      title: "Financials",
      rows: [
        { label: "EMI Affordability", value: inr(d.emi_affordability) },
        { label: "Monthly Net Income", value: inr(d.monthly_net_income) },
        { label: "Company Equity", value: inr(d.company_equity) },
        { label: "Company Debt", value: inr(d.company_debt) },
        { label: "NPA in last 3 yrs", value: d.npa_last_3y },
      ],
    },
    {
      title: "Credit Assessment Workflow",
      rows: [
        { label: "Risk Officer (RO)", value: d.ro_status },
        { label: "Credit Officer (CO)", value: d.co_status },
        { label: "Credit Committee (MCC)", value: d.mcc_status },
        { label: "Due Diligence (DD)", value: d.dd_status },
        { label: "Moderator Approved", value: d.moderator_approved == null ? "" : yn(d.moderator_approved) },
        { label: "DD Moderator Approved", value: d.dd_moderator_approved == null ? "" : yn(d.dd_moderator_approved) },
      ],
    },
    {
      title: "Documents & Timeline",
      rows: [
        { label: "Term Sheet", value: d.has_term_sheet ? "Available" : "Not generated" },
        { label: "DD Report", value: d.has_dd_report ? "Available" : "Not generated" },
        { label: "Created Date", value: d.created_date },
        { label: "Approved Date", value: d.approved_date },
        { label: "DD Approved Date", value: d.dd_approved_date },
        { label: "Last Modified", value: d.modified_date },
        { label: "Remark", value: d.remark },
        { label: "Submissions", value: d.submitted_count },
      ],
    },
  ];
  // Keep only blocks that contain at least one real value (Pipeline files may
  // lack the normalized credit-workflow fields entirely).
  return blocks
    .filter((b) => has(b.rows))
    .map((b) => `<section class="sec"><h2>${esc(b.title)}</h2>${kvTable(b.rows)}</section>`)
    .join("");
}

function sectionHtml(s) {
  const empty =
    (s.type === "records" && !(s.records && s.records.length)) ||
    (s.type === "documents" && !(s.documents && s.documents.length)) ||
    (s.type === "table" && !(s.rows && s.rows.length)) ||
    (s.type === "kv" && !(s.rows && s.rows.length) && !(s.documents && s.documents.length));

  let body = "";
  if (empty) {
    body = `<p class="empty">Not available</p>`;
  } else if (s.type === "kv") {
    body = kvTable(s.rows) + docList(s.documents);
  } else if (s.type === "documents") {
    body = docList(s.documents);
  } else if (s.type === "records") {
    body = s.records
      .map(
        (rec, i) =>
          `<div class="record"><div class="record-no">#${i + 1}</div>${kvTable(
            rec.rows
          )}${docList(rec.documents)}</div>`
      )
      .join("");
  } else if (s.type === "table") {
    body = `<table class="grid"><thead><tr>${s.columns
      .map((c) => `<th>${esc(c)}</th>`)
      .join("")}</tr></thead><tbody>${s.rows
      .map(
        (r) =>
          `<tr>${s.columns.map((c) => `<td>${esc(r[c])}</td>`).join("")}</tr>`
      )
      .join("")}</tbody></table>`;
  }

  return `<section class="sec"><h2>${esc(s.title)}</h2>${body}</section>`;
}

export function generateLoanFilePDF(d) {
  if (!d) return;
  const printedOn = new Date().toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
  const subtitle = [
    formatINRFull(d.loan_amount),
    d.loan_type,
    d.tenor != null ? `${d.tenor} mo` : null,
    d.status || d.app_type,
  ]
    .filter(Boolean)
    .join(" · ");

  const sectionsHtml = (d.sections || []).map(sectionHtml).join("");
  const summaryHtml = summarySections(d);

  const html = `<!doctype html><html><head><meta charset="utf-8">
<title>Loan File ${esc(d.loan_no || d.applicant_no || "")} — ${esc(d.company || "")}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Georgia, "Times New Roman", serif; color: #1a1a1b; margin: 0; padding: 0; }
  .page { padding: 28px 34px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start;
    border-bottom: 3px solid #b3122a; padding-bottom: 14px; margin-bottom: 18px; }
  .brand { font-family: Arial, Helvetica, sans-serif; }
  .brand .name { font-size: 18px; font-weight: 800; color: #111111; letter-spacing: .5px; }
  .brand .tag { font-size: 10px; text-transform: uppercase; letter-spacing: 2px; color: #b3122a; font-weight: 700; }
  .meta { text-align: right; font-size: 10px; color: #6b7280; font-family: Arial, sans-serif; }
  .title { font-family: Arial, Helvetica, sans-serif; margin: 4px 0 22px; }
  .title .loanno { font-size: 11px; color: #6b7280; font-family: monospace; }
  .title h1 { font-size: 22px; font-weight: 800; color: #111111; margin: 4px 0; }
  .title .sub { font-size: 12px; color: #374151; }
  .sec { margin-bottom: 18px; page-break-inside: avoid; }
  .sec h2 { font-family: Arial, Helvetica, sans-serif; font-size: 13px; font-weight: 800; color: #fff;
    background: #111111; padding: 7px 12px; margin: 0 0 8px; border-left: 4px solid #b3122a; }
  table.kv { width: 100%; border-collapse: collapse; font-size: 11px; }
  table.kv th { text-align: left; width: 34%; color: #6b7280; font-weight: 600; padding: 4px 8px;
    vertical-align: top; border-bottom: 1px solid #eef0f4; text-transform: uppercase; font-size: 9px; letter-spacing: .4px; }
  table.kv td { padding: 4px 8px; font-weight: 600; border-bottom: 1px solid #eef0f4; vertical-align: top; }
  table.grid { width: 100%; border-collapse: collapse; font-size: 9.5px; }
  table.grid th { background: #F4F1EA; text-align: left; padding: 5px 6px; border: 1px solid #e5e7eb;
    font-family: Arial, sans-serif; color: #374151; }
  table.grid td { padding: 4px 6px; border: 1px solid #eef0f4; }
  .record { border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px 10px; margin-bottom: 8px; }
  .record-no { font-family: Arial, sans-serif; font-size: 10px; font-weight: 800; color: #b3122a; margin-bottom: 4px; }
  ul.docs { list-style: none; padding: 0; margin: 8px 0 0; font-family: Arial, sans-serif; font-size: 11px; }
  ul.docs li { padding: 4px 0; border-bottom: 1px solid #eef0f4; }
  ul.docs a { color: #b3122a; text-decoration: none; word-break: break-all; }
  .tag { font-size: 8px; font-weight: 700; padding: 1px 5px; border-radius: 3px; font-family: Arial, sans-serif; }
  .tag.signed { background: #d1fae5; color: #047857; }
  .tag.unsigned { background: #fef3c7; color: #b45309; }
  .empty { font-style: italic; color: #9ca3af; font-size: 11px; margin: 4px 0; }
  .foot { margin-top: 24px; border-top: 1px solid #e5e7eb; padding-top: 10px; font-family: Arial, sans-serif;
    font-size: 9px; color: #9ca3af; text-align: center; }
  @page { margin: 16mm 12mm; }
  @media print { .head { position: running(header); } }
</style></head>
<body><div class="page">
  <div class="head">
    <div class="brand"><div class="name">RED FORT CAPITAL</div><div class="tag">Confidential Credit File</div></div>
    <div class="meta">Generated ${esc(printedOn)}<br/>Source: Loan Management System (Live)</div>
  </div>
  <div class="title">
    <div class="loanno">Loan No: ${esc(d.loan_no || d.applicant_no || "—")}</div>
    <h1>${esc(d.company || "")}</h1>
    <div class="sub">${esc(subtitle)}</div>
  </div>
  ${summaryHtml}
  ${sectionsHtml}
  <div class="foot">Red Fort Capital · Strictly Confidential · Data sourced live from the loan management system. Empty sections show "Not available".</div>
</div>
<script>window.onload = function(){ setTimeout(function(){ window.focus(); window.print(); }, 250); };</script>
</body></html>`;

  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
